import React, { useState, useEffect, useMemo, useRef } from "react";
import axiosInstanceExternal from "../../utils/axiosInstanceExternal";
import Select from "react-select";
import Fuse from "fuse.js";
import ReactModal from "react-modal";
import { toast } from "react-toastify";

import MonthChart from "../../components/Dashboard/MonthChart";
import DayChart from "../../components/Dashboard/DayChart";
import CountryMap from "../../components/Dashboard/CountryMap";
import LeadsTable from "../../components/Dashboard/LeadsTable";
import { getSelectedBrandCode, shouldDefaultDashboardMinRelevanceToFifty } from "../../utils/brandContext";

ReactModal.setAppElement("#root");

const OP_OPTIONS = [
    { value: "eq", label: "Equals" },
    { value: "ne", label: "Does not equal" },
    { value: "has", label: "Contains data" },
    { value: "not_has", label: "Does not contain data" },
    { value: "contains", label: "Contains" },
    { value: "not_contains", label: "Does not contain" },
    { value: "begins", label: "Begins with" },
    { value: "not_begins", label: "Does not begin with" },
    { value: "ends", label: "Ends with" },
    { value: "not_ends", label: "Does not end with" },
];

const isNoop = (f) => !f || (f.op === "eq" && (!f.values || f.values.length === 0));
const matchOne = (raw, f) => {
    const s = (raw ?? "").toString().trim().toLowerCase();
    const vals = (f.values || []).map((v) => v.value.toLowerCase());
    switch (f.op) {
        case "has":
            return s.length > 0;
        case "not_has":
            return s.length === 0;
        case "eq":
            return vals.length === 0 ? true : vals.includes(s);
        case "ne":
            return vals.length === 0 ? true : !vals.includes(s);
        case "contains":
            return vals.length === 0 ? true : vals.some((v) => s.includes(v));
        case "not_contains":
            return vals.length === 0 ? true : vals.every((v) => !s.includes(v));
        case "begins":
            return vals.length === 0 ? true : vals.some((v) => s.startsWith(v));
        case "not_begins":
            return vals.length === 0 ? true : vals.every((v) => !s.startsWith(v));
        case "ends":
            return vals.length === 0 ? true : vals.some((v) => s.endsWith(v));
        case "not_ends":
            return vals.length === 0 ? true : vals.every((v) => !s.endsWith(v));
        default:
            return true;
    }
};
const applyFilter = (rows, field, f) => (isNoop(f) ? rows : rows.filter((r) => matchOne(r[field], f)));
const getLocalYmd = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
};

export default function DashboardDomainExternal() {
    const externalAccessToken = localStorage.getItem("externalAccessToken");
    const initialBrandCode = getSelectedBrandCode();

    const user = localStorage.getItem("username");
    const email = localStorage.getItem("email");

    const [allRawData, setAllRawData] = useState({ chart_data_raw: [], detailed_leads_data: [] });
    const [brandCode, setBrandCode] = useState(initialBrandCode);
    const [minRel, setMinRel] = useState(shouldDefaultDashboardMinRelevanceToFifty(initialBrandCode) ? 50 : 0);
    const [loadingData, setLoadingData] = useState(true);

    const [selectedMonth, setSelectedMonth] = useState(null); // YYYY-MM
    const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD
    const [currentPage, setCurrentPage] = useState(0);

    const [searchTerm, setSearchTerm] = useState("");
    const [countryFilter, setCountryFilter] = useState({ op: "eq", values: [] });
    const [categoryFilter, setCategoryFilter] = useState({ op: "eq", values: [] });
    const [selectedCRM, setSelectedCRM] = useState({ value: "*", label: "All CRM Statuses" });

    const crmOptions = useMemo(
        () => [
            { value: "*", label: "All CRM Statuses" },
            { value: "hasOwner", label: "Recorded in CRM" },
            { value: "noOwner", label: "Not Recorded in CRM" },
        ],
        []
    );

    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [recordToUpdate, setRecordToUpdate] = useState({});
    const [recordDetails, setRecordDetails] = useState({});

    const [emailButtonDisabled, setEmailButtonDisabled] = useState(false);
    const [expandButtonDisabled, setExpandButtonDisabled] = useState(false);
    const [leadButtonDisabled, setLeadButtonDisabled] = useState(false);

    const recordsPerPage = 30;

    useEffect(() => {
        if (!user) {
            setLoadingData(false);
            return;
        }
        setLoadingData(true);
        axiosInstanceExternal
            .get(`/external/all_leads_data/${encodeURIComponent(externalAccessToken)}`)
            .then((res) => setAllRawData(res.data))
            .catch(() => setAllRawData({ chart_data_raw: [], detailed_leads_data: [] }))
            .finally(() => setLoadingData(false));
    }, [user, externalAccessToken, brandCode]);

    useEffect(() => {
        const syncBrand = () => {
            const next = getSelectedBrandCode();
            setBrandCode(next);
            setMinRel(shouldDefaultDashboardMinRelevanceToFifty(next) ? 50 : 0);
            setSelectedMonth(null);
            setSelectedDay(null);
            setCurrentPage(0);
        };
        window.addEventListener("brandUpdated", syncBrand);
        window.addEventListener("storage", syncBrand);
        return () => {
            window.removeEventListener("brandUpdated", syncBrand);
            window.removeEventListener("storage", syncBrand);
        };
    }, []);

    const fuse = useMemo(
        () =>
            new Fuse(allRawData.detailed_leads_data, {
                keys: ["domain", "company_name"],
                threshold: 0.1,
                ignoreLocation: true,
                includeScore: true,
            }),
        [allRawData.detailed_leads_data]
    );

    const countryOptions = useMemo(() => {
        const uniq = Array.from(new Set(allRawData.detailed_leads_data.map((r) => r.country).filter(Boolean)));
        return uniq.map((c) => ({ value: c, label: c }));
    }, [allRawData.detailed_leads_data]);

    const categoryOptions = useMemo(() => {
        const uniq = Array.from(new Set(allRawData.detailed_leads_data.map((r) => r.business_category).filter(Boolean)));
        return uniq.map((c) => ({ value: c, label: c }));
    }, [allRawData.detailed_leads_data]);

    // ---- Helper: base filters shared across views (no month/day, no country) ----
    const baseFiltered = useMemo(() => {
        let temp = searchTerm.trim()
            ? fuse.search(searchTerm).map((r) => r.item)
            : [...allRawData.detailed_leads_data];

        // relevance
        temp = temp.filter((lead) => (lead.relevance ?? 0) >= minRel);
        // category
        temp = applyFilter(temp, "business_category", categoryFilter);
        // CRM
        if (selectedCRM.value !== "*") {
            temp = temp.filter((r) => (selectedCRM.value === "hasOwner" ? !!r.dynamics_owner : !r.dynamics_owner));
        }
        return temp;
    }, [allRawData.detailed_leads_data, searchTerm, fuse, minRel, categoryFilter, selectedCRM]);

    // ---- Add temporal selection for views that need it ----
    const withTemporal = useMemo(() => {
        let temp = [...baseFiltered];
        if (selectedDay) {
            temp = temp.filter((lead) => getLocalYmd(lead.created_at) === selectedDay);
        } else if (selectedMonth) {
            temp = temp.filter((lead) => getLocalYmd(lead.created_at).substring(0, 7) === selectedMonth);
        }
        return temp;
    }, [baseFiltered, selectedMonth, selectedDay]);

    // ---- Table dataset: all filters including country and temporal ----
    const tableFiltered = useMemo(() => {
        const base = applyFilter(withTemporal, "country", countryFilter);

        if (!sortField) return base; // cancel → original order

        const sorted = [...base].sort((a, b) => {
            const aRaw = a[sortField];
            const bRaw = b[sortField];

            let diff;
            if (sortField === "created_at" || sortField === "expanded_at") {
                diff = new Date(aRaw) - new Date(bRaw);
            } else if (sortField === "relevance") {
                diff = (aRaw ?? 0) - (bRaw ?? 0);
            } else {
                diff = (aRaw ?? "")
                    .toString()
                    .localeCompare((bRaw ?? "").toString(), undefined, { sensitivity: "base" });
            }
            return sortOrder === "asc" ? diff : -diff;
        });

        return sorted;
    }, [withTemporal, countryFilter, sortField, sortOrder]);

    useEffect(
        () => setCurrentPage(0),
        [tableFiltered, selectedMonth, selectedDay, countryFilter, searchTerm, minRel, categoryFilter, selectedCRM]
    );

    const filteredCount = tableFiltered.length;
    const absoluteTotal = allRawData.detailed_leads_data.length;

    const pageCount = Math.ceil(filteredCount / recordsPerPage) || 1;
    const displayedRecords = tableFiltered.slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage);

    // ---- Month chart: apply country filter, ignore temporal selection, aggregate last 12 months ----
    const { labels: monthLabels, counts: monthCounts, monthYAxisMax } = useMemo(() => {
        const start = new Date();
        start.setDate(1);
        start.setMonth(start.getMonth() - 11);
        const months = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        });

        const baseForMonths = applyFilter(baseFiltered, "country", countryFilter);

        const bucket = {};
        for (const r of baseForMonths) {
            const key = getLocalYmd(r.created_at).substring(0, 7);
            if (!months.includes(key)) continue;
            bucket[key] = (bucket[key] || 0) + 1;
        }
        const counts = months.map((m) => bucket[m] || 0);
        const max = counts.length ? Math.max(...counts) : 0;
        return { labels: months, counts, monthYAxisMax: Math.ceil(max / 10) * 10 };
    }, [baseFiltered, countryFilter]);

    // ---- Day chart: apply country and selected month ----
    const { dayKeys, dayCounts, dayYAxisMax } = useMemo(() => {
        if (!selectedMonth) return { dayKeys: [], dayCounts: [], dayYAxisMax: 0 };
        const [yStr, mStr] = selectedMonth.split("-");
        const y = Number(yStr),
            m = Number(mStr);
        const daysInMonth = new Date(y, m, 0).getDate();
        const keys = Array.from({ length: daysInMonth }, (_, i) => `${selectedMonth}-${String(i + 1).padStart(2, "0")}`);

        let baseDays = applyFilter(baseFiltered, "country", countryFilter);
        baseDays = baseDays.filter((r) => getLocalYmd(r.created_at).substring(0, 7) === selectedMonth);

        const bucket = {};
        for (const r of baseDays) {
            const k = getLocalYmd(r.created_at);
            bucket[k] = (bucket[k] || 0) + 1;
        }
        const counts = keys.map((k) => bucket[k] || 0);
        const max = counts.length ? Math.max(...counts) : 0;
        return { dayKeys: keys, dayCounts: counts, dayYAxisMax: Math.ceil(max / 10) * 10 };
    }, [baseFiltered, countryFilter, selectedMonth]);

    // ---- Map counts: base + temporal (month/day), NO country filter ----
    const countryCounts = useMemo(() => {
        const out = {};
        for (const r of withTemporal) {
            const name = (r.country || "").trim();
            if (!name) continue;
            out[name] = (out[name] || 0) + 1;
        }
        return out;
    }, [withTemporal]);

    const selectedCountryName = useMemo(() => {
        if (countryFilter.op === "eq" && countryFilter.values && countryFilter.values.length === 1) {
            return countryFilter.values[0].value;
        }
        return null;
    }, [countryFilter]);

    // ---- Actions / handlers ----
    function handleSort(field) {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else {
            if (sortOrder === "asc") setSortOrder("desc");
            else if (sortOrder === "desc") setSortField(null);
        }
    }

    async function handleUpdateRecord() {
        try {
            await axiosInstanceExternal.put(`/record/records/${recordToUpdate.record_id}`, recordToUpdate);
            toast.success("Record updated successfully");
            setIsUpdateModalOpen(false);
            setLoadingData(true);
            axiosInstanceExternal
                .get(`/external/all_leads_data/${encodeURIComponent(externalAccessToken)}`)
                .then((res) => setAllRawData(res.data))
                .finally(() => setLoadingData(false));
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update record.");
        }
    }

    async function handleDeleteRecord() {
        if (!recordToDelete) return;
        try {
            await axiosInstanceExternal.delete(`/record/records/${recordToDelete.record_id}`);
            toast.success("Record deleted successfully");
            setIsDeleteModalOpen(false);
            setLoadingData(true);
            axiosInstanceExternal
                .get(`/external/all_leads_data/${encodeURIComponent(externalAccessToken)}`)
                .then((res) => setAllRawData(res.data))
                .finally(() => setLoadingData(false));
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete record.");
        }
    }

    function handleInputChange(e, updateMode = false) {
        const { name, value } = e.target;
        if (updateMode) setRecordToUpdate((prev) => ({ ...prev, [name]: value }));
    }

    function formatDate(date) {
        if (!date) return "";
        const localDate = new Date(date.replace(/ GMT$/, ""));
        const day = String(localDate.getDate()).padStart(2, "0");
        const month = localDate.toLocaleString("en-AU", { month: "short" });
        const year = localDate.getFullYear();
        const hours = String(localDate.getHours()).padStart(2, "0");
        const minutes = String(localDate.getMinutes()).padStart(2, "0");
        return `${day} ${month}, ${year} ${hours}:${minutes}`;
    }

    async function handleEmailData() {
        const formattedRecords = tableFiltered.map((rec) => ({
            ...rec,
            created_at: formatDate(rec.created_at),
            expanded_at: formatDate(rec.expanded_at),
        }));
        setEmailButtonDisabled(true);
        try {
            const resp = await axiosInstanceExternal.post("/record/send_email", { email, records: formattedRecords });
            if (resp.status === 200) toast.success(resp.data.message || "Email sent successfully");
            else toast.error(resp.data.message || "Failed to send email");
        } catch (err) {
            toast.error("Failed to send email: " + (err.response?.data?.message || err.message));
        } finally {
            setEmailButtonDisabled(false);
        }
    }

    const handleRedo = async () => {
        const domainIds = tableFiltered.map((rec) => rec.record_id);
        if (domainIds.length === 0) {
            toast.error("No records to redo");
            return;
        }

        setExpandButtonDisabled(true);
        try {
            const response = await axiosInstanceExternal.post("/record/forceEnhanceExpand", { domain_ids: domainIds });
            if (response.status === 202) {
                toast.success(response.data.message || "Domains added to redo successfully");
            } else {
                toast.error(response.data.message || "Failed to add records to redo");
            }
        } catch (err) {
            toast.error("Failed to add records to redo" + err.response?.data?.message);
        } finally {
            setExpandButtonDisabled(false);
        }
    };

    async function handleCreateLeads() {
        const ids = tableFiltered.map((r) => r.record_id);
        if (!ids.length) {
            toast.info("No records selected");
            return;
        }
        setLeadButtonDisabled(true);
        try {
            await axiosInstanceExternal.post("/record/create_leads_async", { record_ids: ids });
            toast.success("Lead creation started in background.");
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Unknown error";
            toast.error(`Failed to create leads: ${msg}`);
        } finally {
            setLeadButtonDisabled(false);
        }
    }

    async function handleSingleRedo(recordId) {
        setExpandButtonDisabled(true);
        try {
            const resp = await axiosInstanceExternal.post("/record/forceEnhanceExpand", { domain_ids: [recordId] });
            if (resp.status === 202) toast.success(resp.data.message || "Domain added to redo");
            else toast.error(resp.data.message || "Failed to add to redo");
        } catch (err) {
            toast.error("Failed to add to redo" + (err.response?.data?.message ? `: ${err.response.data.message}` : ""));
        } finally {
            setExpandButtonDisabled(false);
        }
    }

    async function handleCreateSingleLead(recordId) {
        setLeadButtonDisabled(true);
        try {
            const { data } = await axiosInstanceExternal.post("/record/create_leads", { record_ids: [recordId] });
            const { created = [], skipped = [], errors = [] } = data;
            const parts = [];
            if (created.length) parts.push(`${created.length} lead${created.length > 1 ? "s" : ""} created`);
            if (skipped.length) parts.push(`${skipped.length} skipped`);
            if (errors.length) parts.push(`${errors.length} error${errors.length > 1 ? "s" : ""}`);
            toast.success(parts.join(", ") || "Done");
        } catch (err) {
            toast.error(`Failed to create lead: ${err.response?.data?.error || err.message || "Unknown error"}`);
        } finally {
            setLeadButtonDisabled(false);
        }
    }

    if (loadingData) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-gray-100">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
                    <p className="text-sm sm:text-base font-semibold text-slate-700">Loading domain data…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 px-3 sm:px-4 md:px-6 py-6">
            <div className="w-full max-w-full mx-auto">
                {user && (
                    <div className="flex flex-col gap-5 sm:gap-6">
                        {/* Controls + Charts */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="p-4 sm:p-6">
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                            Domain Activity
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-600">
                                            Adjust relevance and drill down by month/day.
                                        </p>
                                    </div>

                                    <div className="shrink-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Min relevance</div>
                                        <div className="text-sm font-extrabold text-slate-900">{minRel}%</div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="rel-slider" className="block text-sm font-semibold text-slate-700 mb-2">
                                        Min. relevance: <span className="font-extrabold text-slate-900">{minRel}%</span>
                                    </label>
                                    <input
                                        id="rel-slider"
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={minRel}
                                        onChange={(e) => {
                                            setMinRel(Number(e.target.value));
                                            setSelectedMonth(null);
                                            setSelectedDay(null);
                                            setCurrentPage(0);
                                        }}
                                        className="w-full accent-slate-900"
                                    />
                                </div>

                                <div className="flex justify-center px-1 sm:px-4">
                                    <MonthChart
                                        labels={monthLabels}
                                        counts={monthCounts}
                                        minRel={minRel}
                                        yAxisMax={monthYAxisMax}
                                        onSelect={(m) => {
                                            setSelectedMonth(m);
                                            setSelectedDay(null);
                                            setCurrentPage(0);
                                            if (m) toast.info(`Zoomed into ${m} (relevance ≥ ${minRel}%)`);
                                        }}
                                    />
                                </div>

                                {(selectedMonth || selectedDay) && (
                                    <div className="flex flex-wrap items-center gap-2 mt-4">
                                        {selectedMonth && (
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold">
                                                Month: {selectedMonth}
                                                <button
                                                    onClick={() => {
                                                        setSelectedMonth(null);
                                                        setSelectedDay(null);
                                                        setCurrentPage(0);
                                                    }}
                                                    className="ml-1 rounded-full px-2 py-0.5 bg-white/10 hover:bg-white/20 transition"
                                                    title="Clear month"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        )}
                                        {selectedDay && (
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-600 text-white text-sm font-semibold">
                                                Day: {selectedDay}
                                                <button
                                                    onClick={() => {
                                                        setSelectedDay(null);
                                                        setCurrentPage(0);
                                                    }}
                                                    className="ml-1 rounded-full px-2 py-0.5 bg-white/10 hover:bg-white/20 transition"
                                                    title="Clear day"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                )}

                                {selectedMonth && (
                                    <div className="mt-6 flex justify-center px-1 sm:px-4">
                                        <DayChart
                                            month={selectedMonth}
                                            fullDayKeys={dayKeys}
                                            counts={dayCounts}
                                            minRel={minRel}
                                            yAxisMax={dayYAxisMax}
                                            onSelect={(dayKey) => {
                                                setSelectedDay(dayKey);
                                                setCurrentPage(0);
                                                if (dayKey) toast.info(`Showing ${dayKey} (relevance ≥ ${minRel}%)`);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Map */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                            <div className="p-4 sm:p-6">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                            Map View by Country
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-600">
                                            Counts reflect current filters
                                            {selectedMonth ? ` for ${selectedMonth}` : ""}
                                            {selectedDay ? ` on ${selectedDay}` : ""}. Click a country to toggle a filter.
                                        </p>
                                    </div>

                                    {selectedCountryName && (
                                        <button
                                            onClick={() => setCountryFilter({ op: "eq", values: [] })}
                                            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 transition"
                                        >
                                            Clear Country
                                        </button>
                                    )}
                                </div>

                                <CountryMap
                                    countryCounts={countryCounts}
                                    selectedCountryName={selectedCountryName}
                                    onSelectCountry={(name) => {
                                        if (name) setCountryFilter({ op: "eq", values: [{ value: name, label: name }] });
                                        else setCountryFilter({ op: "eq", values: [] });
                                        setCurrentPage(0);
                                    }}
                                />
                            </div>
                        </section>

                        {/* Actions + Filters + Table */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="p-4 sm:p-6">
                                {/* Filters */}
                                <div className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4">
                                    <div className="flex-[2] min-w-[260px] w-full">
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                                        <input
                                            type="text"
                                            placeholder="Search by domain or company…"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm sm:text-base text-slate-900 placeholder:text-slate-400
                      focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300 transition"
                                        />
                                    </div>

                                    <div className="flex items-end">
                                        <FilterPopover
                                            btnText="Category"
                                            filter={categoryFilter}
                                            setFilter={setCategoryFilter}
                                            options={categoryOptions}
                                        />
                                    </div>

                                    <div className="flex items-end">
                                        <FilterPopover
                                            btnText="Country"
                                            filter={countryFilter}
                                            setFilter={setCountryFilter}
                                            options={countryOptions}
                                        />
                                    </div>

                                    <div className="w-56 sm:w-64">
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">CRM Status</label>
                                        <Select
                                            value={selectedCRM}
                                            onChange={setSelectedCRM}
                                            options={crmOptions}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder="Filter by CRM…"
                                            styles={{
                                                control: (p) => ({
                                                    ...p,
                                                    minHeight: 44,
                                                    height: 44,
                                                    borderColor: "#e2e8f0",
                                                    boxShadow: "none",
                                                    borderRadius: 12,
                                                    "&:hover": { borderColor: "#cbd5e1" },
                                                }),
                                                valueContainer: (p) => ({ ...p, height: 44 }),
                                                input: (p) => ({ ...p, margin: 0, padding: 0 }),
                                                option: (p, s) => ({
                                                    ...p,
                                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                                    color: s.isSelected ? "white" : "#0f172a",
                                                }),
                                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Table */}
                                <LeadsTable
                                    records={displayedRecords}
                                    filteredCount={filteredCount}
                                    absoluteTotal={absoluteTotal}
                                    sortField={sortField}
                                    sortOrder={sortOrder}
                                    onSort={handleSort}
                                    formatDate={formatDate}
                                    onOpenDetails={(rec) => {
                                        setRecordDetails(rec);
                                        setIsDetailsModalOpen(true);
                                    }}
                                    onOpenUpdate={(rec) => {
                                        setRecordToUpdate(rec);
                                        setIsUpdateModalOpen(true);
                                    }}
                                    onOpenDelete={(rec) => {
                                        setRecordToDelete(rec);
                                        setIsDeleteModalOpen(true);
                                    }}
                                    onAddToQueue={handleSingleRedo}
                                    onCreateLead={handleCreateSingleLead}
                                    expandDisabled={expandButtonDisabled}
                                    leadDisabled={leadButtonDisabled}
                                    pageCount={pageCount}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                    recordsPerPage={recordsPerPage}
                                    actionHidden={true}
                                />
                            </div>
                        </section>

                        {/* Update Modal */}
                        <ReactModal
                            isOpen={isUpdateModalOpen}
                            onRequestClose={() => setIsUpdateModalOpen(false)}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Update Record</h2>
                                    <p className="text-sm text-slate-600 mt-1">Edit fields and save changes.</p>
                                </div>
                                <button
                                    onClick={() => setIsUpdateModalOpen(false)}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>

                            {renderFields(recordToUpdate, true)}

                            <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsUpdateModalOpen(false)}
                                    className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateRecord}
                                    className="py-2 px-4 rounded-xl bg-amber-600 font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300 transition"
                                >
                                    Update
                                </button>
                            </div>
                        </ReactModal>

                        {/* Details Modal */}
                        <ReactModal
                            isOpen={isDetailsModalOpen}
                            onRequestClose={() => setIsDetailsModalOpen(false)}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(44rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Record Details</h2>
                                    <p className="text-sm text-slate-600 mt-1">Read-only view of the record.</p>
                                </div>
                                <button
                                    onClick={() => setIsDetailsModalOpen(false)}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>

                            {renderFields(recordDetails, false, true)}
                        </ReactModal>

                        {/* Delete Modal */}
                        <ReactModal
                            isOpen={isDeleteModalOpen}
                            onRequestClose={() => setIsDeleteModalOpen(false)}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(28rem,calc(100vw-2rem))] outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Confirm Deletion</h2>
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>

                            <p className="text-slate-700 mb-5 text-sm sm:text-base">Are you sure you want to delete this record?</p>

                            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="py-2 px-4 rounded-xl bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteRecord}
                                    className="py-2 px-4 rounded-xl bg-red-600 text-white font-semibold shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition"
                                >
                                    Delete
                                </button>
                            </div>
                        </ReactModal>
                    </div>
                )}
            </div>
        </div>
    );

    function renderFields(record, updateMode, readOnly = false) {
        const fields = [
            { label: "Domain", name: "domain", isLink: true },
            { label: "Company Name", name: "company_name" },
            { label: "Summary", name: "summary" },
            { label: "Emails", name: "emails" },
            { label: "Phone", name: "phone" },
            { label: "LinkedIn", name: "linkedin", isLink: true },
            { label: "Street Address", name: "street_address" },
            { label: "City", name: "city" },
            { label: "State/Province", name: "state_province" },
            { label: "Zip/Postal Code", name: "zip_postal_code" },
            { label: "Country", name: "country" },
            { label: "Business Category", name: "business_category" },
            { label: "Keywords", name: "keywords" },
            { label: "Search Terms", name: "search_terms" },
            { label: "Relevance", name: "relevance" },
            { label: "Research Similar Flag", name: "research_similar_flag" },
        ];

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((field) => {
                    let value = record[field.name] ?? "";
                    if (field.isDate) value = formatDate(value);

                    if (readOnly && field.isLink && value) {
                        return (
                            <div key={field.name} className="sm:col-span-2">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                                <a
                                    href={value.startsWith("http") ? value : `https://${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full break-all rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                >
                                    {value}
                                </a>
                            </div>
                        );
                    }

                    return (
                        <div key={field.name} className={field.name === "summary" ? "sm:col-span-2" : ""}>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                            <input
                                name={field.name}
                                type="text"
                                value={value}
                                onChange={(e) => !readOnly && handleInputChange(e, updateMode)}
                                readOnly={readOnly || field.readOnly}
                                className={[
                                    "w-full rounded-xl border px-3 py-2 text-sm text-slate-900 transition",
                                    readOnly || field.readOnly
                                        ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                                        : "border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300",
                                ].join(" ")}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

function FilterPopover({ btnText, filter, setFilter, options }) {
    const [open, setOpen] = useState(false);
    const [draftOp, setDraftOp] = useState(filter.op);
    const [draftVals, setDraftVals] = useState(filter.values || []);
    const ref = useRef(null);

    useEffect(() => {
        if (open) {
            setDraftOp(filter.op);
            setDraftVals(filter.values || []);
        }
    }, [open, filter.op, filter.values]);

    useEffect(() => {
        const onDoc = (e) => {
            if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const disabled = draftOp === "has" || draftOp === "not_has";

    return (
        <div className="relative">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Filter</label>
            <button
                type="button"
                className="h-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                onClick={() => setOpen((o) => !o)}
            >
                Filter: {btnText}
                <span className="ml-2 text-slate-400" aria-hidden="true">
                    ▾
                </span>
            </button>

            {open && (
                <div
                    ref={ref}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute right-0 mt-2 w-80 rounded-2xl bg-white border border-slate-200 shadow-xl p-4 z-20"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-extrabold text-slate-900">Filter by {btnText}</div>
                        <button
                            className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                            onClick={() => setOpen(false)}
                            type="button"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="space-y-3">
                        <Select
                            value={OP_OPTIONS.find((o) => o.value === draftOp)}
                            onChange={(op) => setDraftOp(op.value)}
                            options={OP_OPTIONS}
                            classNamePrefix="react-select"
                            placeholder="Equals"
                            styles={{
                                control: (p) => ({
                                    ...p,
                                    minHeight: 44,
                                    height: 44,
                                    borderColor: "#e2e8f0",
                                    boxShadow: "none",
                                    borderRadius: 12,
                                    "&:hover": { borderColor: "#cbd5e1" },
                                }),
                                option: (p, s) => ({
                                    ...p,
                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                    color: s.isSelected ? "white" : "#0f172a",
                                }),
                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                            }}
                        />

                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isDisabled={disabled}
                            value={draftVals}
                            onChange={(vals) => setDraftVals(vals || [])}
                            options={options}
                            classNamePrefix="react-select"
                            placeholder="Select…"
                            isClearable={false}
                            styles={{
                                control: (p) => ({
                                    ...p,
                                    minHeight: 44,
                                    borderColor: "#e2e8f0",
                                    boxShadow: "none",
                                    borderRadius: 12,
                                    "&:hover": { borderColor: "#cbd5e1" },
                                    opacity: disabled ? 0.65 : 1,
                                }),
                                option: (p, s) => ({
                                    ...p,
                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                    color: s.isSelected ? "white" : "#0f172a",
                                }),
                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                            }}
                        />
                    </div>

                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            className="px-3 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition focus:outline-none focus:ring-2 focus:ring-slate-300"
                            onClick={() => setDraftVals([])}
                            type="button"
                        >
                            Clear
                        </button>
                        <button
                            className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            onClick={() => {
                                setFilter({ op: draftOp, values: draftVals });
                                setOpen(false);
                            }}
                            type="button"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
